import request from '@/utils/request'

/** 路径/请求体中的 ID 统一为字符串，避免雪花 Long 在 JS 中精度丢失 */
function asId(id) {
  if (id == null || id === '') return id
  return String(id)
}

function pageApi(path) {
  return {
    page: (params = {}) => {
      const { current, pageNum, pageSize, ...rest } = params
      return request.get(`${path}/page`, {
        params: {
          pageNum: pageNum || current || 1,
          pageSize: pageSize || 10,
          ...rest
        }
      })
    },
    get: (id) => request.get(`${path}/${asId(id)}`),
    create: (data) => request.post(path, data),
    update: (id, data) => request.put(path, { ...data, id: asId(id) }),
    remove: (id) => request.delete(`${path}/${asId(id)}`)
  }
}

export const statsApi = {
  get: (module) => request.get(`/erp/stats/${module}`)
}

export const authApi = {
  captcha: () => request.get('/auth/captcha'),
  login: (data) => request.post('/auth/login', data),
  logout: () => request.post('/auth/logout'),
  profile: () => request.get('/auth/info'),
  changePassword: (data) => request.post('/auth/change-password', data)
}

export const dashboardApi = {
  summary: (params) => request.get('/erp/dashboard/summary', { params }),
  todos: () => request.get('/erp/dashboard/todos'),
  flows: (params) => request.get('/erp/dashboard/flows', { params })
}

export const customerApi = {
  ...pageApi('/erp/customer'),
  stats: () => request.get('/erp/customer/stats'),
  follow: (id, data) => request.post(`/erp/customer/${asId(id)}/follow`, data),
  export: (params) => request.get('/erp/customer/export', { params, responseType: 'blob' }),
  detail: (id) => request.get(`/erp/customer/${asId(id)}/detail`),
  syncFromXp: () => request.post('/erp/customer/sync-from-xp')
}

export const opportunityApi = {
  ...pageApi('/erp/opportunity'),
  win: (id, autoSign = false) => request.post(`/erp/opportunity/${asId(id)}/win`, null, { params: { autoSign } }),
  lost: (id, reason) => request.post(`/erp/opportunity/${asId(id)}/lost`, { reason }),
  quotes: (id) => request.get(`/erp/opportunity/${asId(id)}/quotes`)
}

/** 售后与服务工单（售后/非标设计/合同外运营） */
export const serviceTicketApi = {
  ...pageApi('/erp/service-ticket'),
  stats: (year) => request.get('/erp/service-ticket/stats', { params: { year } }),
  transition: (id, action, remark) =>
    request.post(`/erp/service-ticket/${asId(id)}/transition`, { action, remark }),
  analysis: (params) => request.get('/erp/service-ticket/analysis', { params }),
  items: (id) => request.get(`/erp/service-ticket/${asId(id)}/items`),
  searchDevices: (customerId, keyword) =>
    request.get('/erp/service-ticket/devices', { params: { customerId: asId(customerId), keyword } }),
  servicePlans: (customerId) =>
    request.get('/erp/service-ticket/service-plans', { params: { customerId: asId(customerId) } })
}

/** 业务变更历史（商机 / 报价 / 合同） */
export const changeLogApi = {
  list: (bizType, bizId) => request.get(`/erp/change-log/${bizType}/${asId(bizId)}`),
  groups: (bizType, bizId) => request.get(`/erp/change-log/${bizType}/${asId(bizId)}/groups`)
}
export const quoteApi = {
  ...pageApi('/erp/opportunity'),
  simulate: (data) => request.post('/erp/quote/simulate', data)
}

/** 销售报价单（含明细/快照/审批/版本/转合同） */
export const quoteOrderApi = {
  ...pageApi('/erp/quote-order'),
  stats: (year) => request.get('/erp/quote-order/stats', { params: { year } }),
  detail: (id) => request.get(`/erp/quote-order/${asId(id)}/detail`),
  items: (id) => request.get(`/erp/quote-order/${asId(id)}/items`),
  versions: (id) => request.get(`/erp/quote-order/${asId(id)}/versions`),
  saveWithItems: (data) => request.post('/erp/quote-order/save-with-items', data),
  buildItems: (data) => request.post('/erp/quote-order/build-items', data),
  submit: (id) => request.post(`/erp/quote-order/${asId(id)}/submit`),
  approve: (id, pass, reason) =>
    request.post(`/erp/quote-order/${asId(id)}/approve`, { pass, reason }),
  revise: (id) => request.post(`/erp/quote-order/${asId(id)}/revise`),
  voidQuote: (id) => request.post(`/erp/quote-order/${asId(id)}/void`),
  toContract: (id) => request.post(`/erp/quote-order/${asId(id)}/to-contract`),
  removeWithItems: (id) => request.delete(`/erp/quote-order/${asId(id)}/with-items`)
}

/** 销售项目套餐配置 */
export const materialPackageApi = {
  items: (packageId) => request.get(`/erp/material-package/${asId(packageId)}/items`),
  saveItems: (packageId, items) => request.post(`/erp/material-package/${asId(packageId)}/items`, items),
  summary: (packageId) => request.get(`/erp/material-package/${asId(packageId)}/summary`),
  selectable: (params) => request.get('/erp/material-package/selectable', { params })
}

/** 合同销售项目明细 */
export const contractItemApi = {
  byContract: (contractId) => request.get(`/erp/contract-item/by-contract/${asId(contractId)}`),
  summary: (contractId) => request.get(`/erp/contract-item/summary/${asId(contractId)}`)
}

/** 销售项目多维统计分析 */
export const salesItemAnalysisApi = {
  summary: (params) => request.get('/erp/sales-item-analysis/summary', { params }),
  group: (params) => request.get('/erp/sales-item-analysis/group', { params }),
  trend: (params) => request.get('/erp/sales-item-analysis/trend', { params }),
  items: (params) => request.get('/erp/sales-item-analysis/items', { params }),
  dimensions: () => request.get('/erp/sales-item-analysis/dimensions')
}
export const contractApi = {
  ...pageApi('/erp/contract'),
  sign: (id) => request.post(`/erp/contract/${asId(id)}/sign`),
  saveWithItems: (data) => request.post('/erp/contract/save-with-items', data),
  items: (id) => request.get(`/erp/contract/${asId(id)}/items`),
  saveItems: (id, items) => request.post(`/erp/contract/${asId(id)}/items`, items),
  buildItems: (data) => request.post('/erp/contract/build-items', data),
  // 分期付款计划
  installments: (id) => request.get(`/erp/contract/${asId(id)}/installments`),
  saveInstallments: (id, rows, needInstallment = true) =>
    request.post(`/erp/contract/${asId(id)}/installments`, rows, { params: { needInstallment } }),
  suggestInstallments: (id, periods = 3) =>
    request.get(`/erp/contract/${asId(id)}/installments/suggest`, { params: { periods } }),
  // 发货计划
  shipmentPlans: (id) => request.get(`/erp/contract/${asId(id)}/shipment-plans`),
  saveShipmentPlans: (id, rows, needShipment = true) =>
    request.post(`/erp/contract/${asId(id)}/shipment-plans`, rows, { params: { needShipment } }),
  suggestShipmentPlans: (id) => request.get(`/erp/contract/${asId(id)}/shipment-plans/suggest`)
}
export const agentApi = pageApi('/erp/agent-profile')
export const salesTargetApi = pageApi('/erp/sales-target')
export const salesPerfApi = {
  view: (params) => request.get('/erp/analysis/sales-perf', { params })
}
export const materialApi = pageApi('/erp/material')
export const inventoryApi = pageApi('/erp/inventory-record')
export const projectApi = {
  ...pageApi('/erp/project'),
  profit: (id) => request.get(`/erp/project/${asId(id)}/profit`),
  detail: (id) => request.get(`/erp/project/${asId(id)}/detail`),
  follow: (id, data) => request.post(`/erp/project/${asId(id)}/follow`, data),
  settle: (id) => request.post(`/erp/project/${asId(id)}/settle`),
  complete: (id) => request.post(`/erp/project/${asId(id)}/complete`)
}
export const shipmentApi = {
  ...pageApi('/erp/shipment'),
  shipByPlan: (planId, data) => request.post(`/erp/shipment/ship-by-plan/${asId(planId)}`, data),
  receive: (id, data) => request.post(`/erp/shipment/${asId(id)}/receive`, data)
}
export const deliveryApi = pageApi('/erp/delivery')
export const invoiceApi = pageApi('/erp/invoice')
export const receivableApi = pageApi('/erp/receivable')
export const receiptApi = {
  ...pageApi('/erp/receipt'),
  register: (data) => request.post('/erp/receipt/register', data)
}
export const expenseApi = {
  ...pageApi('/erp/expense'),
  saveWithDetails: (data) => request.post('/erp/expense/save-with-details', data)
}
export const payableApi = pageApi('/erp/payable')
export const paymentApi = {
  page: (params) => pageApi('/erp/payment').page(params),
  register: (data) => request.post('/erp/payment/register', data)
}

export const pnlApi = {
  view: (params) => request.get('/erp/analysis/pnl', { params }),
  export: (params) => request.get('/erp/analysis/pnl/export', { params })
}
export const cashflowApi = {
  view: (params) => request.get('/erp/analysis/cashflow', { params })
}
export const profitProjectApi = {
  analyze: (params) => request.get(`/erp/project/${asId(params.projectId)}/profit`),
  list: () => request.get('/erp/analysis/profit/project'),
  refresh: (year) => request.post('/erp/analysis/refresh', null, { params: { year } })
}
export const profitCustomerApi = {
  analyze: (params) => request.get('/erp/analysis/profit/customer', { params })
}
export const profitAgentApi = {
  analyze: (params) => request.get('/erp/analysis/profit/agent', { params })
}
export const salesRegionApi = {
  analyze: (params) => request.get('/erp/analysis/region', { params })
}
export const efficiencyApi = {
  analyze: (params) => request.get('/erp/analysis/efficiency', { params })
}
export const reportApi = {
  view: (params) => request.get('/erp/analysis/report/annual', { params }),
  export: (params) => request.get('/erp/analysis/report/annual/export', { params })
}
export const forecastApi = {
  view: (params) => request.get('/erp/analysis/forecast', { params })
}

export const reminderApi = pageApi('/erp/reminder')
export const rulesApi = {
  get: () => request.get('/erp/rule-version/active'),
  save: (data) => request.put('/erp/rule-version/active', data),
  publish: (data) => request.post('/erp/rule-version/publish', data)
}

export const roleApi = pageApi('/sys/role')
export const sysUserApi = {
  ...pageApi('/sys/user'),
  resetPassword: (id, newPassword) => request.post(`/sys/user/${asId(id)}/reset-password`, { newPassword }),
  updateStatus: (id, status) => request.put(`/sys/user/${asId(id)}/status`, { status }),
  updateRoles: (id, roleIds) => request.put(`/sys/user/${asId(id)}/roles`, { roleIds })
}
export const permissionApi = {
  ...pageApi('/sys/permission'),
  menuTree: () => request.get('/sys/permission/menu-tree')
}
export const rolePermApi = {
  get: (roleId) => request.get(`/sys/role/${asId(roleId)}/permissions`),
  assign: (roleId, permCodes) => request.put(`/sys/role/${asId(roleId)}/permissions`, { permCodes })
}
export const fieldPermApi = {
  list: (params) => request.get('/sys/field-permission/page', { params }),
  save: (data) => request.post('/sys/field-permission/batch', data)
}
/** 通用附件上传：返回 {name, url} */
export const fileApi = {
  upload: (file) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post('/erp/file/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }
}

export const dictApi = {
  list: (type) => request.get(`/sys/dict/${type}`)
}
